import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Check, X, Flag } from "lucide-react";
import { toast } from "sonner";

export const TrustQueueCard = () => {
  const [kyc, setKyc] = useState([]);
  const [flags, setFlags] = useState([]);

  const load = useCallback(() => {
    api.get("/trust/kyc/queue").then(({ data }) => setKyc(data)).catch(() => {});
    api.get("/trust/fraud-queue").then(({ data }) => setFlags(data.flags || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (uid, approve) => {
    try {
      await api.post(`/trust/kyc/${uid}/decide`, { approve, note: approve ? "" : "Rejected by admin" });
      toast.success(approve ? "Seller verified ✅" : "KYC rejected");
      load();
    } catch { toast.error("Action failed"); }
  };

  const resolveDispute = async (orderId, refund) => {
    try {
      await api.post(`/trust/disputes/${orderId}/resolve`, { refund, note: refund ? "Refunded by admin" : "Dispute rejected by admin" });
      toast.success(refund ? "Buyer refunded 💸" : "Dispute rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (kyc.length === 0 && flags.length === 0) return null;
  return (
    <div data-testid="trust-queue-card" className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6">
      <div className="flex items-center gap-3">
        <span className="rounded-lg bg-neutral-900 p-2 text-emerald-400"><ShieldCheck size={18} /></span>
        <div className="font-bold text-sm uppercase tracking-widest">Trust & Safety</div>
      </div>
      {kyc.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">KYC pending ({kyc.length})</div>
          <ul className="mt-2 space-y-2">
            {kyc.map((k) => (
              <li key={k.user_id} className="flex flex-wrap items-center gap-3 text-xs" data-testid={`kyc-row-${k.user_id}`}>
                <span className="text-neutral-200 font-semibold">{k.full_name}</span>
                <span className="text-neutral-500">{k.email} · {k.phone}</span>
                <a href={k.id_document_url?.startsWith("/api/documents/") ? `${process.env.REACT_APP_BACKEND_URL}${k.id_document_url}` : k.id_document_url} target="_blank" rel="noreferrer" className="text-[#007AFF] underline" data-testid={`kyc-doc-link-${k.user_id}`}>ID doc</a>
                <span className={k.bank_name_match ? "text-emerald-400" : "text-amber-400"}>
                  bank name {k.bank_name_match ? "matches" : k.bank_account_name ? "MISMATCH" : "not connected"}
                </span>
                <button data-testid={`kyc-approve-${k.user_id}`} onClick={() => decide(k.user_id, true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-bold">
                  <Check className="h-3 w-3" /> Verify
                </button>
                <button data-testid={`kyc-reject-${k.user_id}`} onClick={() => decide(k.user_id, false)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] font-bold">
                  <X className="h-3 w-3" /> Reject
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {flags.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1.5">
            <Flag className="h-3 w-3" /> Fraud flags ({flags.length})
          </div>
          <ul className="mt-2 space-y-1.5">
            {flags.slice(0, 8).map((f) => (
              <li key={f.id} className="flex items-center gap-3 text-xs" data-testid={`flag-${f.id}`}>
                <span className={`uppercase font-bold ${f.type === "dispute" ? "text-amber-400" : "text-[#FF3B30]"}`}>{f.type}</span>
                <span className="text-neutral-400 flex-1 truncate">{f.reason}</span>
                {f.type === "dispute" && f.order_id ? (
                  <>
                    <button data-testid={`dispute-refund-${f.id}`} onClick={() => resolveDispute(f.order_id, true)}
                      className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-bold">
                      Refund buyer
                    </button>
                    <button data-testid={`dispute-reject-${f.id}`} onClick={() => resolveDispute(f.order_id, false)}
                      className="px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 hover:text-white font-bold">
                      Reject
                    </button>
                  </>
                ) : (
                  <button onClick={async () => { await api.post(`/trust/fraud-queue/${f.id}/resolve`).catch(() => {}); load(); }}
                    className="text-neutral-500 hover:text-white underline">resolve</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
